import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import "./MyBookingsPage.css";
import { fetchBookingsByEmail } from "./clientEventService";

const API_BASE = "/api/public/events";

/**
 * ============================
 * SAFE DEBUG LOGGER
 * ============================
 * Will NOT log in production
 */
const MyBookingsPage = () => {

const navigate = useNavigate();

const [email, setEmail] = useState("");
const [inputEmail, setInputEmail] = useState("");
const [bookings, setBookings] = useState([]);
const [loading, setLoading] = useState(false);
const [checkedAuth, setCheckedAuth] = useState(false);
const [now, setNow] = useState(new Date());

const [alert, setAlert] = useState({
 show: false,
 type: "info",
 message: ""
});


/* tick every minute so button state stays current */
useEffect(() => {
 const timer = setInterval(() => setNow(new Date()), 60000);
 return () => clearInterval(timer);
}, []);


/* ============================
CHECK LOGGED-IN USER
============================ */
useEffect(() => {

 const storedUser = localStorage.getItem("user");

 if (storedUser) {

 try {

 const parsed = JSON.parse(storedUser);

 if (parsed?.email) {

 setEmail(parsed.email);

 fetchBookings(parsed.email);

 }

 } catch (error) {}

 }

 setCheckedAuth(true);

}, []);


/* ============================
SHOW ALERT
============================ */
const showAlert = (type, message) => {

 setAlert({ show: true, type, message });

 setTimeout(() => setAlert(prev => ({ ...prev, show: false })), 4000);

};


/* ============================
FETCH BOOKINGS
============================ */
const fetchBookings = async (targetEmail) => {

 if (!targetEmail) return;

 try {

 setLoading(true);

 const response = await fetchBookingsByEmail(targetEmail);

 if (response.status === 200) {

 const bookingsData = response.data?.data || response.data || [];
 setBookings(bookingsData);

 } else {

 setBookings([]);

 }

 } catch (error) {

 setBookings([]);

 } finally {

 setLoading(false);

 }

};


/* ============================
FORMAT HELPERS
============================ */
const formatDateSafe = (value) => {

 try {

 if (!value) return "-";

 const date = new Date(value);

 if (isNaN(date)) return value;

 return date.toLocaleDateString("en-IN", {
 weekday: "long",
 day: "numeric",
 month: "long",
 year: "numeric"
 });

 } catch (error) {

 return value;
 }

};

const formatTimeSafe = (value) => {

 try {

 if (!value) return "-";

 const date = new Date(value);

 if (isNaN(date)) return value;

 return date.toLocaleTimeString("en-IN", {
 hour: "2-digit",
 minute: "2-digit",
 hour12: true
 });

 } catch (error) {

 return value;
 }

};


/* ============================
GUEST EMAIL SUBMIT
============================ */
const handleEmailSubmit = () => {

 if (!inputEmail.trim()) {

 showAlert("warning", "Please enter email address");

 return;
 }

 setEmail(inputEmail);

 fetchBookings(inputEmail);

};


/* ============================
JOIN BUTTON STATE
============================
 'upcoming' -> more than 15 min before start
 'active' -> within 15 min of start and before end
 'expired' -> after end time
 'cancelled' -> booking cancelled
============================ */
const getJoinState = (booking) => {
 if (booking.cancelled || booking.bookingStatus === 'CANCELLED') return 'cancelled';

 const startTime = new Date(booking.eventStartTime || booking.eventDate);
 if (isNaN(startTime)) return 'active'; // fallback: show join

 const durationMs = (booking.eventDuration || 60) * 60 * 1000;
 const endTime = new Date(startTime.getTime() + durationMs);
 const diffMs = startTime - now;

 if (now > endTime) return 'expired';
 if (diffMs <= 15 * 60 * 1000) return 'active';
 return 'upcoming';
};


/* ============================
JOIN SESSION
============================ */
const handleJoin = (booking) => {

 if (booking.zoomLink) {

 window.open(booking.zoomLink, "_blank");

 } else {

 showAlert("warning", "Zoom link is not available yet.");

 }

};


return (
<>
 <Navbar />

 {alert.show && (
 <div className={`custom-alert ${alert.type}`}>
 {alert.message}
 </div>
 )}

 <div className="bookings-container">

 {/* HEADER */}
 <div className="bookings-header">

 <button
 className="back-btn"
 onClick={() => navigate("/events")}
 >
 ← Back to events
 </button>

 <p className="subtitle">
 View and manage your booked sessions
 </p>

 </div>


 {/* EMAIL CARD */}
 {checkedAuth && !email && (
 <div className="email-card">

 <h3>Find your bookings</h3>

 <div className="email-input-group">

 <input
 type="email"
 placeholder="Enter your email address"
 value={inputEmail}
 onChange={(e) => setInputEmail(e.target.value)}
 />

 <button onClick={handleEmailSubmit}>
 View Bookings
 </button>

 </div>

 </div>
 )}


 {/* LOADING */}
 {loading && (
 <div className="status-card">
 Loading your bookings...
 </div>
 )}


 {/* BOOKINGS */}
 {!loading && email && bookings.length > 0 && (

 <div className="bookings-list">

 {bookings.map((booking) => {
 const isCancelled = booking.cancelled || booking.bookingStatus === 'CANCELLED';
 const joinState = getJoinState(booking);

 return (
 <div className="booking-card" key={booking.bookingId}>

 {/* DATE / TIME HIGHLIGHT BANNER */}
 <div className="booking-datetime-banner">
 <div className="datetime-block">
 <span className="datetime-icon">📅</span>
 <span className="datetime-label">Date</span>
 <span className="datetime-value">
 {formatDateSafe(booking.eventStartTime || booking.eventDate)}
 </span>
 </div>
 <div className="datetime-block">
 <span className="datetime-icon">⏰</span>
 <span className="datetime-label">Time</span>
 <span className="datetime-value">
 {formatTimeSafe(booking.eventStartTime || booking.eventTime)}
 </span>
 </div>
 </div>

 <div className="booking-card-body">

 <div className="booking-header">
 <h3>{booking.eventTitle}</h3>
 <span className={`badge ${
 isCancelled ? "cancelled" : 
 booking.bookingStatus === 'CONFIRMED' ? "confirmed" : "pending"
 }`}>
 {isCancelled ? "Cancelled" : 
 booking.bookingStatus === 'CONFIRMED' ? "Confirmed" : 
 booking.bookingStatus || "Confirmed"}
 </span>
 </div>

 {booking.eventDescription && (
 <p className="description">{booking.eventDescription}</p>
 )}

 <div className="details-list">

 {booking.eventDuration && (
 <div className="detail-row">
 <span className="detail-label">Duration:</span>
 <span className="detail-value">{booking.eventDuration} minutes</span>
 </div>
 )}

 {booking.speakerName && (
 <div className="detail-row">
 <span className="detail-label">Speaker:</span>
 <span className="detail-value">{booking.speakerName}</span>
 </div>
 )}

 {booking.platform && (
 <div className="detail-row">
 <span className="detail-label">Platform:</span>
 <span className="detail-value">{booking.platform}</span>
 </div>
 )}

 </div>

 <button
 onClick={() => joinState === 'active' && handleJoin(booking)}
 className={`join-btn join-btn--${joinState}`}
 disabled={joinState !== 'active'}
 >
 {joinState === 'cancelled' && 'Session Cancelled'}
 {joinState === 'upcoming' && '🔒 Link will be enabled 15 minutes before start'}
 {joinState === 'active' && '▶ Join Session'}
 {joinState === 'expired' && 'Session Expired'}
 </button>

 </div>

 </div>
 );
 })}

 </div>

 )}


 {/* EMPTY */}
 {!loading && email && bookings.length === 0 && (

 <div className="status-card">

 <h3>No bookings found</h3>

 <p>You haven't booked any sessions yet.</p>

 <button
 className="primary-btn"
 onClick={() => navigate("/events")}
 >
 Browse Events
 </button>

 </div>

 )}

 </div>

</>
);

};

export default MyBookingsPage;